/**
 * PublicBooking - Public booking page for embedding as iframe
 * 
 * Features:
 * - No login required
 * - Service selection → Date/Time selection → Customer data → Direct booking
 * - Account creation + Welcome email
 * - Dark theme (configurable)
 * - Embeddable as iframe
 * 
 * URL: /buchen or /buchen?service=ems-training
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Clock, User, Phone, Mail, ChevronLeft, ChevronRight, Check, Zap, Snowflake, Dumbbell, MessageCircle, ArrowLeft } from 'lucide-react';
import AppDownloadBanner from '@/components/public/AppDownloadBanner';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { PhoneInput } from '@/components/ui/phone-input';
import AddonServicePopup from '@/components/portal/AddonServicePopup';
import ConsentSignSection from '@/components/portal/ConsentSignSection';
import { et, etp, etTime, EMBED_LANG, EMBED_LOCALE, EMBED_WEEKDAYS_SHORT } from '../i18n/embedLang';
import { applyPublicTenant } from '../utils/publicTenant';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Mandanten-Kontext aus der URL (?tenant=) für alle Requests dieser Seite setzen
applyPublicTenant();

// Service icons mapping
const SERVICE_ICONS = {
  'ems': Zap,
  'kryo': Snowflake,
  'cryo': Snowflake,
  'personal': Dumbbell,
  'training': Dumbbell,
  'beratung': MessageCircle,
  'consult': MessageCircle,
  'default': Calendar
};

const getServiceIcon = (serviceName) => {
  const name = serviceName.toLowerCase();
  for (const [key, Icon] of Object.entries(SERVICE_ICONS)) {
    if (name.includes(key)) return Icon;
  }
  return SERVICE_ICONS.default;
};

// =============================================
// MAIN COMPONENT
// =============================================

const PublicBooking = () => {
  // Multi-Studio: Step 0 = Studio-Auswahl (überspringen bei 1 Studio).
  // Wenn /buchen?studio=<slug> wird das Studio vorbelegt → direkt Step 1.
  const [step, setStep] = useState(0); // 0: Studio, 1: Service, 2: Date/Time, 3: Customer
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  // Belegte Slots (voll) → als Warteliste anklickbar (10/2026)
  const [fullSlots, setFullSlots] = useState([]);
  const [waitlistMode, setWaitlistMode] = useState(false);
  const [waitlistComplete, setWaitlistComplete] = useState(false);
  // Teilschritte innerhalb der Kundendaten (Step 3): Kontakt → AGB → Haftung → Einverständnis → Bestätigen
  const [custStep, setCustStep] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  // Add-on-Popup nach erfolgreicher Buchung (Parität mit Kundenportal, 07/2026)
  const [showAddonPopup, setShowAddonPopup] = useState(false);
  const [bookedAddon, setBookedAddon] = useState(null);
  
  // Embed mode - hide header/footer when ?embed=true
  const [isEmbedMode, setIsEmbedMode] = useState(false);
  
  // Customer form data
  const [customerData, setCustomerData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    referral_source_id: '',
    accepted_terms: false
  });
  const [withPartner, setWithPartner] = useState(false);
  const [partnerData, setPartnerData] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [referralSources, setReferralSources] = useState([]);
  
  // Terms dialogs
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  
  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  // Einverständnis-Erklärung (Kryo/EMS) — Pflicht bei entsprechenden Services
  const [consentForm, setConsentForm] = useState(null);
  const [consentPayload, setConsentPayload] = useState(null);
  // Haftungsausschluss — IMMER Pflicht (jede Probetraining-Buchung)
  const [haftungForm, setHaftungForm] = useState(null);
  const [haftungPayload, setHaftungPayload] = useState(null);
  // Read bg parameter from URL for embed mode (same as pricing page)
  const embedBgParam = new URLSearchParams(window.location.search).get('bg');
  const embedBg = isEmbedMode ? (embedBgParam || 'transparent') : null;

  const theme = {
    bg: isEmbedMode ? (embedBg === 'transparent' ? 'transparent' : embedBg) : (settings?.public_booking_bg || '#000000'),
    cardBg: isEmbedMode ? '#171717' : (settings?.public_booking_card_bg || '#1a1a1a'),
    accent: '#EAB308',
    text: '#ffffff',
    textMuted: '#A3A3A3',
    border: isEmbedMode ? '#262626' : '#2a2a2a'
  };

  // Einverständnis-Formular laden, wenn der gewählte Service es verlangt (Kryo/EMS)
  useEffect(() => {
    setConsentPayload(null);
    if (selectedService?.consent_form_id) {
      axios.get(`${API}/public/consent-forms/${selectedService.consent_form_id}?lang=${EMBED_LANG}`)
        .then(res => setConsentForm(res.data))
        .catch(() => setConsentForm(null));
    } else {
      setConsentForm(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  // Haftungsausschluss immer laden (jede Buchung braucht die Unterschrift)
  useEffect(() => {
    axios.get(`${API}/public/consent-forms/haftung?lang=${EMBED_LANG}`)
      .then(res => setHaftungForm(res.data))
      .catch(() => setHaftungForm(null));
  }, []);

  // Lead-Quellen laden (Pflichtfeld "Wie hast du von uns erfahren?")
  useEffect(() => {
    axios.get(`${API}/referral-sources/public`)
      .then(res => setReferralSources(Array.isArray(res.data) ? res.data : []))
      .catch(() => setReferralSources([]));
  }, []);

  // Load settings and services
  useEffect(() => {
    // Check for embed mode
    const params = new URLSearchParams(window.location.search);
    const embedMode = params.get('embed') === 'true';
    setIsEmbedMode(embedMode);
    
    // Set body background in embed mode for seamless iframe integration
    if (embedMode) {
      const bgParam = params.get('bg') || 'transparent';
      document.body.style.backgroundColor = bgParam === 'transparent' ? 'transparent' : bgParam;
      document.documentElement.style.backgroundColor = bgParam === 'transparent' ? 'transparent' : bgParam;
      document.body.style.background = bgParam === 'transparent' ? 'transparent' : bgParam;
      document.documentElement.style.background = bgParam === 'transparent' ? 'transparent' : bgParam;
      document.documentElement.classList.add('dark');
      document.documentElement.classList.add('embed-mode');
      // postMessage auto-height — robuster Resize-Handler (siehe
      // Bug-Report 05/2026: PublicPricingPage sendete zu kleine Höhen).
      // Statt nur `body.scrollHeight` nehmen wir das Maximum aller Höhen-
      // Quellen und beobachten die Höhe per ResizeObserver mit 50 ms
      // Debounce — fängt async-Inhalte (Slots, Trainer, Bilder) sauber ab.
      const sendHeight = () => {
        try {
          const doc = document.documentElement;
          const body = document.body;
          const height = Math.max(
            doc.scrollHeight,
            doc.offsetHeight,
            body ? body.scrollHeight : 0,
            body ? body.offsetHeight : 0,
            Math.ceil(doc.getBoundingClientRect().height)
          );
          window.parent.postMessage({ type: 'resize', height }, '*');
        } catch (e) { /* sandboxed */ }
      };
      let debTimer;
      const debounced = () => {
        window.clearTimeout(debTimer);
        debTimer = window.setTimeout(sendHeight, 50);
      };
      const ro = new ResizeObserver(debounced);
      ro.observe(document.documentElement);
      if (document.body) ro.observe(document.body);
      window.addEventListener('load', debounced);
      window.addEventListener('resize', debounced);
      // Mehrfach-Burst für asynchron geladenen Content.
      setTimeout(sendHeight, 100);
      setTimeout(sendHeight, 500);
      setTimeout(sendHeight, 1500);
    }
    
    const loadData = async () => {
      try {
        const [settingsRes, studiosRes] = await Promise.all([
          axios.get(`${API}/public/settings`),
          axios.get(`${API}/public/locations`).catch(() => ({ data: [] })),
        ]);

        setSettings(settingsRes.data);
        const studiosData = studiosRes.data;
        const studioArr = Array.isArray(studiosData) ? studiosData : (studiosData?.studios || []);
        const studioList = studioArr.filter(s => s && s.studio_id);
        setStudios(studioList);

        // URL-Parameter ?studio=<slug|id> → vorbelegen
        const params = new URLSearchParams(window.location.search);
        const studioParam = (params.get('studio') || '').trim().toLowerCase();
        let preStudio = null;
        if (studioParam) {
          preStudio = studioList.find(
            s => (s.slug === studioParam || s.studio_id === studioParam) && !s.coming_soon
          );
        }
        // Nur 1 buchbares Studio → automatisch wählen, Schritt 0 überspringen
        const bookable = studioList.filter(s => !s.coming_soon);
        if (!preStudio && studioList.length === 1 && bookable.length === 1) {
          preStudio = bookable[0];
        }
        if (preStudio) {
          setSelectedStudio(preStudio);
          await loadStudioData(preStudio.studio_id, params);
        } else {
          // Mehrere Studios → bleibe auf Step 0
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error(et('Fehler beim Laden der Daten'));
        setLoading(false);
      }
    };

    const loadStudioData = async (studioId, params) => {
      try {
        const [servicesRes, trainersRes] = await Promise.all([
          axios.get(`${API}/public/services`, { params: { studio_id: studioId } }),
          axios.get(`${API}/public/trainers`, { params: { studio_id: studioId } }).catch(() => ({ data: [] })),
        ]);
        const bookableServices = (servicesRes.data || []).filter(s => s.is_active !== false);
        setServices(bookableServices);
        setTrainers((trainersRes.data || []).filter(t => t.is_active !== false));

        // Check URL for pre-selected service
        const serviceSlug = params?.get('service');
        if (serviceSlug) {
          const preselected = bookableServices.find(s =>
            s.name.toLowerCase().includes(serviceSlug.toLowerCase()) ||
            s.id === serviceSlug
          );
          if (preselected) {
            setSelectedService(preselected);
            setStep(2);
          } else {
            setStep(1);
          }
        } else {
          setStep(1);
        }
      } catch (err) {
        console.error('Error loading studio data:', err);
        toast.error(et('Fehler beim Laden der Studio-Daten'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Studio nachträglich wechseln (z.B. zurück-Button auf Step 0)
  const handleSelectStudio = useCallback(async (studio) => {
    if (studio?.coming_soon) return;
    setSelectedStudio(studio);
    setLoading(true);
    try {
      const [servicesRes, trainersRes] = await Promise.all([
        axios.get(`${API}/public/services`, { params: { studio_id: studio.studio_id } }),
        axios.get(`${API}/public/trainers`, { params: { studio_id: studio.studio_id } }).catch(() => ({ data: [] })),
      ]);
      setServices((servicesRes.data || []).filter(s => s.is_active !== false));
      setTrainers((trainersRes.data || []).filter(t => t.is_active !== false));
      setSelectedService(null);
      setSelectedTrainer(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableSlots([]);
      setStep(1);
    } catch (err) {
      toast.error(et('Fehler beim Laden der Studio-Daten'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reCAPTCHA script when settings are available
  useEffect(() => {
    if (settings?.recaptcha_enabled && settings?.recaptcha_site_key && !recaptchaLoaded) {
      // Remove any existing reCAPTCHA scripts first
      const existingScripts = document.querySelectorAll('script[src*="recaptcha"]');
      existingScripts.forEach(s => s.remove());
      
      // Also remove any existing grecaptcha container
      const existingContainers = document.querySelectorAll('.grecaptcha-badge, iframe[src*="recaptcha"]');
      existingContainers.forEach(c => c.remove());
      
      // Reset grecaptcha
      if (window.grecaptcha) {
        delete window.grecaptcha;
      }
      
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit&hl=${EMBED_LANG}&_=${Date.now()}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      // Expose callbacks for reCAPTCHA
      window.onRecaptchaLoad = () => {
        console.log('reCAPTCHA loaded');
        setRecaptchaLoaded(true);
      };
      window.onRecaptchaChange = (token) => {
        console.log('reCAPTCHA token received');
        setRecaptchaToken(token);
      };
      window.onRecaptchaExpired = () => {
        console.log('reCAPTCHA expired');
        setRecaptchaToken(null);
      };
    }
  }, [settings, recaptchaLoaded]);

  // Render reCAPTCHA when we're on step 3 and script is loaded
  useEffect(() => {
    if (step === 3 && recaptchaLoaded && settings?.recaptcha_enabled && settings?.recaptcha_site_key) {
      const container = document.getElementById('recaptcha-container');
      if (container && window.grecaptcha && window.grecaptcha.render) {
        // Check if not already rendered
        if (container.childElementCount === 0) {
          try {
            window.grecaptcha.render('recaptcha-container', {
              sitekey: settings.recaptcha_site_key,
              callback: window.onRecaptchaChange,
              'expired-callback': window.onRecaptchaExpired,
              theme: 'dark'
            });
          } catch (e) {
            console.log('reCAPTCHA already rendered or error:', e);
          }
        }
      }
    }
  }, [step, custStep, recaptchaLoaded, settings]);

  // Load availability when date or service changes
  useEffect(() => {
    if (selectedService && selectedDate) {
      loadAvailability();
    }
  }, [selectedService, selectedDate]);

  const loadAvailability = async () => {
    if (!selectedService || !selectedDate) return;
    
    try {
      // Format date in local timezone (YYYY-MM-DD)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      console.log('Loading availability for:', dateStr, selectedService.id);
      const response = await axios.get(`${API}/public/availability`, {
        params: {
          date: dateStr,
          service_id: selectedService.id,
          trainer_id: selectedTrainer?.id || 'all',
          studio_id: selectedStudio?.studio_id || undefined,
          _t: Date.now() // Cache buster
        }
      });
      
      console.log('Availability response:', response.data);
      setAvailableSlots(response.data.slots || []);
      setFullSlots(response.data.full_slots || []);
    } catch (error) {
      console.error('Error loading availability:', error);
      setAvailableSlots([]);
      setFullSlots([]);
    }
  };

  // Warteliste: belegte Zeit → vollständige Registrierung + Eintrag.
  const submitWaitlist = async () => {
    if (settings?.recaptcha_enabled && !recaptchaToken) {
      toast.error(et('Bitte bestätigen Sie, dass Sie kein Roboter sind'));
      return;
    }
    setBooking(true);
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      await axios.post(`${API}/public/waitlist`, {
        service_id: selectedService.id,
        trainer_id: selectedTrainer?.id || null,
        date: dateStr,
        time: selectedTime,
        studio_id: selectedStudio?.studio_id || null,
        customer: { ...customerData, accepted_terms_at: new Date().toISOString() },
        recaptcha_token: recaptchaToken || null,
        language: EMBED_LANG,
      });
      setWaitlistComplete(true);
      setBookingComplete(true);
      setStep(4);
    } catch (error) {
      console.error('Waitlist error:', error);
      toast.error(error.response?.data?.detail || et('Eintrag fehlgeschlagen'));
      if (window.grecaptcha) {
        window.grecaptcha.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setBooking(false);
    }
  };

  const loadTerms = async () => {
    try {
      const response = await axios.get(`${API}/policies/terms`);
      setTermsContent(response.data.content || et('AGB nicht verfügbar'));
      setShowTerms(true);
    } catch (error) {
      setTermsContent(et('AGB konnten nicht geladen werden'));
      setShowTerms(true);
    }
  };

  const loadPrivacy = async () => {
    try {
      const response = await axios.get(`${API}/policies/privacy`);
      setPrivacyContent(response.data.content || et('Datenschutzerklärung nicht verfügbar'));
      setShowPrivacy(true);
    } catch (error) {
      setPrivacyContent(et('Datenschutzerklärung konnte nicht geladen werden'));
      setShowPrivacy(true);
    }
  };

  const handleBooking = async () => {
    // Validate
    if (!customerData.first_name || !customerData.last_name) {
      toast.error(et('Bitte geben Sie Ihren Namen ein'));
      return;
    }
    if (!customerData.email || !customerData.email.includes('@')) {
      toast.error(et('Bitte geben Sie eine gültige E-Mail-Adresse ein'));
      return;
    }
    if (!customerData.phone || customerData.phone.length < 5) {
      toast.error(et('Bitte geben Sie eine gültige Telefonnummer ein'));
      return;
    }
    if (!customerData.accepted_terms) {
      toast.error(et('Bitte akzeptieren Sie die AGB und Datenschutzerklärung'));
      return;
    }
    const _isTrial = !!(selectedService && (selectedService.is_trial || selectedService.once_per_customer || /probe/i.test(selectedService.name || '')));
    if (_isTrial && !customerData.referral_source_id) {
      toast.error(et('Bitte gib an, wie du auf uns aufmerksam geworden bist'));
      return;
    }
    // Warteliste-Modus: Registrierung reicht (keine Unterschriften/Partner nötig).
    if (waitlistMode) {
      await submitWaitlist();
      return;
    }
    if (withPartner && (!partnerData.first_name || !(partnerData.email || partnerData.phone))) {
      toast.error(et('Bitte Vorname und E-Mail oder Telefonnummer des Partners angeben'));
      return;
    }
    if (haftungForm && !haftungPayload) {
      toast.error(et('Bitte unterschreiben Sie den Haftungsausschluss'));
      return;
    }
    if (selectedService?.consent_form_id && consentForm && !consentPayload) {
      toast.error(et('Bitte füllen Sie die Einverständnis-Erklärung aus und unterschreiben Sie'));
      return;
    }
    // Validate reCAPTCHA if enabled
    if (settings?.recaptcha_enabled && !recaptchaToken) {
      toast.error(et('Bitte bestätigen Sie, dass Sie kein Roboter sind'));
      return;
    }
    
    setBooking(true);
    
    try {
      // Format date in local timezone (YYYY-MM-DD)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.post(`${API}/public/book`, {
        service_id: selectedService.id,
        trainer_id: selectedTrainer?.id || null,
        date: dateStr,
        time: selectedTime,
        // Multi-Studio: gewähltes Studio mitsenden
        studio_id: selectedStudio?.studio_id || null,
        customer: {
          ...customerData,
          accepted_terms_at: new Date().toISOString()
        },
        recaptcha_token: recaptchaToken || null,
        // Einverständnis-Erklärungen — beim Buchen unterschrieben (Haftung immer + ggf. Kryo/EMS)
        consent: consentPayload || null,
        consents: [haftungPayload, consentPayload].filter(Boolean),
        // Empfehlungsprogramm: Ref-Code aus URL (?ref=REF-XXXXXX)
        referral_code: new URLSearchParams(window.location.search).get('ref') || null,
        // Embed-Sprache → preferred_language des neuen Kunden + E-Mail-Sprache
        language: EMBED_LANG,
        // Partner-Probetraining: zweiter Teilnehmer wird eingeladen
        partner: (withPartner && partnerData.first_name) ? partnerData : null
      });
      
      setBookingDetails(response.data);
      setBookingComplete(true);
      setStep(4);
      // Success card is shown - no need for additional toast

      // Add-on-Angebot kurz nach dem Erfolgs-Screen zeigen (wie im Portal)
      if (response.data?.appointment_id) {
        setTimeout(() => setShowAddonPopup(true), 1500);
      }
      
      // Send conversion event to parent window (for analytics tracking on www.strongfit.ch)
      try {
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'strongfit_conversion',
            event_type: 'booking_completed',
            metadata: {
              service_id: selectedService?.id,
              service_name: selectedService?.name,
            }
          }, '*');
        }
      } catch (e) {
        // Silently ignore postMessage errors
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || et('Buchung fehlgeschlagen'));
      // Reset reCAPTCHA on error
      if (window.grecaptcha) {
        window.grecaptcha.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setBooking(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty days for padding
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString(EMBED_LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg, fontFamily: isEmbedMode ? "'Inter', sans-serif" : undefined }}>
        {isEmbedMode && <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');`}</style>}
        <div className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full" style={{ borderColor: theme.accent }} />
      </div>
    );
  }

  // Booking complete
  if (bookingComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.bg, fontFamily: isEmbedMode ? "'Inter', sans-serif" : undefined }}>
        {isEmbedMode && <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@300;400;500;600;700&display=swap'); h1,h2,h3,h4,h5,h6 { font-family: 'Space Grotesk', sans-serif; }`}</style>}
        <Toaster position="top-center" />
        <Card className="max-w-md w-full text-center" style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
          <CardContent className="p-8">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: theme.accent }}
            >
              <Check className="h-8 w-8 text-black" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: theme.text }}>
              {waitlistComplete ? et('Auf der Warteliste!') : et('Termin gebucht!')}
            </h2>
            <p className="mb-6" style={{ color: theme.textMuted }}>
              {waitlistComplete
                ? et('Wir haben dich auf die Warteliste gesetzt. Sobald ein Platz frei wird, buchen wir dich automatisch ein und informieren dich.')
                : et('Vielen Dank für Ihre Buchung. Sie erhalten in Kürze eine Bestätigung per E-Mail.')}
            </p>
            
            <div className="text-left p-4 rounded-lg mb-6" style={{ backgroundColor: isEmbedMode ? 'transparent' : theme.bg, border: isEmbedMode ? '1px solid #1a1a1a' : 'none' }}>
              <div className="flex items-center gap-2 mb-2" style={{ color: theme.text }}>
                <Calendar className="h-4 w-4" style={{ color: theme.accent }} />
                <span>{formatDate(selectedDate)}</span>
              </div>
              <div className="flex items-center gap-2 mb-2" style={{ color: theme.text }}>
                <Clock className="h-4 w-4" style={{ color: theme.accent }} />
                <span>{etTime(selectedTime)}</span>
              </div>
              <div className="flex items-center gap-2" style={{ color: theme.text }}>
                <Dumbbell className="h-4 w-4" style={{ color: theme.accent }} />
                <span>{selectedService?.name}</span>
              </div>
              {bookedAddon && (
                <div className="flex items-center gap-2 mt-2" style={{ color: theme.text }} data-testid="public-booked-addon-row">
                  <Check className="h-4 w-4" style={{ color: theme.accent }} />
                  <span>+ {bookedAddon.name}{bookedAddon.time ? ` (${etTime(bookedAddon.time)})` : ''}</span>
                </div>
              )}
            </div>
            
            <p className="text-sm" style={{ color: theme.textMuted }}>
              {et('Eine Willkommens-E-Mail mit Ihren Zugangsdaten wurde an')} <strong style={{ color: theme.accent }}>{customerData.email}</strong>{EMBED_LANG === 'de' ? ' gesendet.' : '.'}
            </p>
          </CardContent>
        </Card>

        <AddonServicePopup
          open={showAddonPopup}
          onClose={() => setShowAddonPopup(false)}
          publicAppointmentId={bookingDetails?.appointment_id}
          onAddonBooked={(res, chosen) => {
            setBookedAddon({
              name: chosen?.addon_service?.name || res?.appointment?.service_name || et('Zusatzleistung'),
              time: chosen?.addon_start_time || null,
            });
          }}
        />
      </div>
    );
  }

  // Kundendaten-Teilschritte (Step 3) dynamisch aufbauen
  const isTrialService = !!(selectedService && (selectedService.is_trial || selectedService.once_per_customer || /probe/i.test(selectedService.name || '')));
  const baseSteps = (studios.length > 1 ? [{ key: 'studio', label: et('Studio') }] : []).concat([
    { key: 'service', label: et('Angebot') },
    { key: 'datetime', label: et('Termin') },
  ]);
  const custSteps = [{ key: 'contact', label: et('Kontakt') }, { key: 'terms', label: et('AGB') }]
    .concat(!waitlistMode && haftungForm ? [{ key: 'haftung', label: et('Haftung') }] : [])
    .concat(!waitlistMode && consentForm ? [{ key: 'consent', label: et('Einverständnis') }] : [])
    .concat([{ key: 'review', label: et('Bestätigen') }]);
  const allSteps = baseSteps.concat(custSteps);
  const safeCustStep = Math.max(0, Math.min(custStep, custSteps.length - 1));
  const custKey = custSteps[safeCustStep]?.key;
  const currentIndex = step <= 2
    ? baseSteps.findIndex(s => s.key === (step === 0 ? 'studio' : step === 1 ? 'service' : 'datetime'))
    : baseSteps.length + safeCustStep;
  const goNextCust = () => {
    if (custKey === 'contact') {
      if (!customerData.first_name || !customerData.last_name) { toast.error(et('Bitte geben Sie Ihren Namen ein')); return; }
      if (!customerData.email || !customerData.email.includes('@')) { toast.error(et('Bitte geben Sie eine gültige E-Mail-Adresse ein')); return; }
      if (!customerData.phone || customerData.phone.length < 5) { toast.error(et('Bitte geben Sie eine gültige Telefonnummer ein')); return; }
      if (isTrialService && !customerData.referral_source_id) { toast.error(et('Bitte gib an, wie du auf uns aufmerksam geworden bist')); return; }
      if (withPartner && (!partnerData.first_name || !(partnerData.email || partnerData.phone))) { toast.error(et('Bitte Vorname und E-Mail oder Telefonnummer des Partners angeben')); return; }
    }
    if (custKey === 'terms' && !customerData.accepted_terms) { toast.error(et('Bitte akzeptieren Sie die AGB und Datenschutzerklärung')); return; }
    if (custKey === 'haftung' && haftungForm && !haftungPayload) { toast.error(et('Bitte unterschreiben Sie den Haftungsausschluss')); return; }
    if (custKey === 'consent' && consentForm && !consentPayload) { toast.error(et('Bitte füllen Sie die Einverständnis-Erklärung aus und unterschreiben Sie')); return; }
    setCustStep(safeCustStep + 1);
  };

  return (
    <div className={`min-h-screen ${isEmbedMode ? 'p-2' : 'p-4'}`} style={{ backgroundColor: theme.bg, fontFamily: isEmbedMode ? "'Inter', sans-serif" : undefined }}>
      {isEmbedMode && <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@300;400;500;600;700&display=swap'); h1,h2,h3,h4,h5,h6,.font-semibold,.font-bold { font-family: 'Space Grotesk', sans-serif; }`}</style>}
      <Toaster position="top-center" />
      
      <div className="max-w-2xl mx-auto">
        {/* App-Download-Banner (Handy: passender Store, Desktop: beide) */}
        <AppDownloadBanner />
        {/* Header - hidden in embed mode */}
        {!isEmbedMode && (
          <div className="text-center mb-8">
            {settings?.logo_url && (
              <img src={settings.logo_url} alt={settings.studio_name} className="h-12 mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-bold" style={{ color: theme.text }}>
              {et('Probetraining buchen')}
            </h1>
            <p style={{ color: theme.textMuted }}>
              {step === 0 && et('Wo möchtest du trainieren?')}
              {step === 1 && et('Wählen Sie Ihr Probetraining')}
              {step === 2 && et('Wählen Sie Datum und Uhrzeit')}
              {step === 3 && (
                custKey === 'contact' ? et('Ihre Kontaktdaten')
                  : custKey === 'terms' ? et('AGB & Datenschutz')
                  : custKey === 'haftung' ? et('Haftungsausschluss')
                  : custKey === 'consent' ? et('Einverständnis-Erklärung')
                  : et('Bestätigen & Buchen'))}
            </p>
            {step > 0 && selectedStudio && (
              <button
                onClick={() => setStep(0)}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all hover:bg-white/5"
                style={{ color: theme.accent, borderColor: theme.accent + '60' }}
                data-testid="booking-studio-chip"
              >
                <Calendar className="h-3 w-3" />
                {selectedStudio.short_name || selectedStudio.name}
                <span style={{ color: theme.textMuted, fontSize: 10 }}>{et('· ändern')}</span>
              </button>
            )}
          </div>
        )}

        {/* Progress Steps - inkl. Kundendaten-Teilschritte (Kontakt/AGB/Haftung/Einverständnis/Bestätigen) */}
        <div className={`flex items-center justify-center gap-1.5 flex-wrap ${isEmbedMode ? 'mb-4' : 'mb-8'}`} data-testid="booking-progress">
          {allSteps.map((st, idx) => (
            <React.Fragment key={st.key}>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all"
                style={{
                  backgroundColor: idx <= currentIndex ? theme.accent : theme.cardBg,
                  color: idx <= currentIndex ? '#000' : theme.textMuted,
                  borderColor: theme.border,
                  borderWidth: idx > currentIndex ? '1px' : 0,
                }}
                title={st.label}
              >
                {idx < currentIndex ? <Check className="h-3.5 w-3.5" /> : idx + 1}
              </div>
              {idx < allSteps.length - 1 && (
                <div
                  className="w-6 h-1 rounded"
                  style={{ backgroundColor: idx < currentIndex ? theme.accent : theme.border }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Studio-Auswahl (Multi-Studio) */}
        {step === 0 && (
          <div className="grid gap-4" data-testid="booking-studio-list">
            {studios.length === 0 ? (
              <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
                <CardContent className="p-6 text-center" style={{ color: theme.textMuted }}>
                  {et('Aktuell sind keine Studios verfügbar.')}
                </CardContent>
              </Card>
            ) : (
              studios.map((studio) => {
                const isComingSoon = !!studio.coming_soon;
                return (
                <Card
                  key={studio.studio_id}
                  className={isComingSoon ? 'cursor-not-allowed' : 'cursor-pointer transition-all hover:scale-[1.02]'}
                  style={{
                    backgroundColor: theme.cardBg,
                    borderColor: !isComingSoon && selectedStudio?.studio_id === studio.studio_id ? theme.accent : theme.border,
                    borderWidth: '2px',
                  }}
                  onClick={isComingSoon ? undefined : () => handleSelectStudio(studio)}
                  data-testid={`booking-studio-${studio.slug}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${theme.accent}20` }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6" style={{ color: theme.accent }}>
                        <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h6"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                        {studio.name}
                        {isComingSoon ? (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide"
                            style={{ backgroundColor: `${theme.accent}25`, color: theme.accent, border: `1px solid ${theme.accent}60` }}
                            data-testid={`booking-studio-coming-soon-${studio.slug}`}
                          >
                            {et('Coming Soon')}
                          </span>
                        ) : studio.is_default && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${theme.accent}25`, color: theme.accent }}>
                            {et('HAUPTSITZ')}
                          </span>
                        )}
                      </h3>
                      {studio.address && (
                        <p className="text-sm truncate" style={{ color: theme.textMuted }}>
                          {studio.address}
                        </p>
                      )}
                      {isComingSoon ? (
                        <p className="text-xs mt-0.5 font-medium" style={{ color: theme.textMuted }}>
                          {et('Eröffnet bald – Buchung noch nicht möglich')}
                        </p>
                      ) : studio.phone && (
                        <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
                          📞 {studio.phone}
                        </p>
                      )}
                    </div>
                    {!isComingSoon && <ChevronRight className="h-5 w-5 flex-shrink-0" style={{ color: theme.textMuted }} />}
                  </CardContent>
                </Card>
                );
              })
            )}
          </div>
        )}

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div className="grid gap-4">
            {services.map((service) => {
              const ServiceIcon = getServiceIcon(service.name);
              return (
                <Card 
                  key={service.id}
                  className="cursor-pointer transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: theme.cardBg, 
                    borderColor: selectedService?.id === service.id ? theme.accent : theme.border,
                    borderWidth: '2px'
                  }}
                  onClick={() => {
                    setSelectedService(service);
                    setStep(2);
                  }}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${theme.accent}20` }}
                    >
                      <ServiceIcon className="h-6 w-6" style={{ color: theme.accent }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold" style={{ color: theme.text }}>{service.name}</h3>
                      <p className="text-sm" style={{ color: theme.textMuted }}>
                        {service.duration_minutes || service.duration} {et('Min.')} • {service.points_cost || service.price || 0} {service.points_cost ? et('Punkte') : 'CHF'}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5" style={{ color: theme.textMuted }} />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Step 2: Date/Time Selection */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Back button */}
            <Button 
              variant="ghost" 
              onClick={() => setStep(1)}
              className="gap-2"
              style={{ color: theme.textMuted }}
            >
              <ArrowLeft className="h-4 w-4" />
              {et('Zurück zur Service-Auswahl')}
            </Button>

            {/* Selected service info */}
            <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
              <CardContent className="p-4 flex items-center gap-4">
                {(() => {
                  const ServiceIcon = getServiceIcon(selectedService?.name || '');
                  return (
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${theme.accent}20` }}
                    >
                      <ServiceIcon className="h-5 w-5" style={{ color: theme.accent }} />
                    </div>
                  );
                })()}
                <div>
                  <h3 className="font-semibold" style={{ color: theme.text }}>{selectedService?.name}</h3>
                  <p className="text-sm" style={{ color: theme.textMuted }}>{etp('{0} Minuten', [selectedService?.duration_minutes || selectedService?.duration])}</p>
                </div>
              </CardContent>
            </Card>

            {/* Calendar */}
            <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    style={{ color: theme.textMuted }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-medium" style={{ color: theme.text }}>
                    {currentMonth.toLocaleDateString(EMBED_LOCALE, { month: 'long', year: 'numeric' })}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    style={{ color: theme.textMuted }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {EMBED_WEEKDAYS_SHORT.map((day) => (
                    <div key={day} className="text-center text-xs py-2" style={{ color: theme.textMuted }}>
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Days */}
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth(currentMonth).map((day, idx) => {
                    const isSelectable = isDateSelectable(day);
                    const isSelected = day && selectedDate && 
                      day.toDateString() === selectedDate.toDateString();
                    
                    return (
                      <button
                        key={idx}
                        disabled={!isSelectable}
                        onClick={() => day && isSelectable && setSelectedDate(day)}
                        className={`
                          h-10 rounded-lg text-sm font-medium transition-all
                          ${!day ? 'invisible' : ''}
                          ${isSelectable ? 'hover:opacity-80' : 'opacity-30 cursor-not-allowed'}
                        `}
                        style={{
                          backgroundColor: isSelected ? theme.accent : 'transparent',
                          color: isSelected ? '#000' : (isSelectable ? theme.text : theme.textMuted)
                        }}
                      >
                        {day?.getDate()}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Time slots */}
            {selectedDate && (
              <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base" style={{ color: theme.text }}>
                    {etp('Verfügbare Zeiten am {0}', [formatDate(selectedDate)])}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {availableSlots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => {
                            setWaitlistMode(false);
                            setSelectedTime(slot.time);
                            if (slot.trainer_id) {
                              // Bug-Fix 07/2026: slot.trainer_id ist eine user_id,
                              // trainers[] enthält staff.id → find() schlug immer fehl
                              // und die Buchung landete beim falschen Trainer.
                              setSelectedTrainer(
                                trainers.find(t => t.id === slot.trainer_id) || { id: slot.trainer_id }
                              );
                            }
                            setCustStep(0);
                            setStep(3);
                          }}
                          className="px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                          style={{
                            backgroundColor: selectedTime === slot.time && !waitlistMode ? theme.accent : `${theme.accent}20`,
                            color: selectedTime === slot.time && !waitlistMode ? '#000' : theme.text
                          }}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-4" style={{ color: theme.textMuted }} data-testid="public-no-free-slots">
                      {fullSlots.length > 0
                        ? et('An diesem Tag sind alle Zeiten belegt. Setze dich unten auf die Warteliste – wir buchen dich automatisch ein, sobald ein Platz frei wird.')
                        : et('Keine verfügbaren Zeiten an diesem Tag')}
                    </p>
                  )}

                  {/* Belegte Zeiten → Warteliste (10/2026) */}
                  {fullSlots.length > 0 && (
                    <div className="mt-5" data-testid="public-waitlist-section">
                      <p className="text-xs mb-2" style={{ color: theme.textMuted }}>
                        {et('Belegt – auf die Warteliste setzen (automatische Einbuchung, sobald frei):')}
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        {fullSlots.map((slot) => (
                          <button
                            key={`full-${slot.time}`}
                            data-testid={`public-full-slot-${slot.time}`}
                            onClick={() => {
                              setWaitlistMode(true);
                              setSelectedTime(slot.time);
                              if (slot.trainer_id) {
                                setSelectedTrainer(
                                  trainers.find(t => t.id === slot.trainer_id) || { id: slot.trainer_id }
                                );
                              }
                              setCustStep(0);
                              setStep(3);
                            }}
                            className="px-3 py-2 rounded-lg text-sm font-medium line-through transition-all hover:scale-105"
                            style={{
                              backgroundColor: selectedTime === slot.time && waitlistMode ? theme.accent : 'transparent',
                              color: selectedTime === slot.time && waitlistMode ? '#000' : theme.textMuted,
                              border: `1px dashed ${theme.border}`
                            }}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 3: Customer Data */}
        {step === 3 && (
          <div className="space-y-6">
            {/* Back button */}
            <Button 
              variant="ghost" 
              onClick={() => { if (safeCustStep > 0) setCustStep(safeCustStep - 1); else setStep(2); }}
              className="gap-2"
              style={{ color: theme.textMuted }}
              data-testid="booking-cust-back"
            >
              <ArrowLeft className="h-4 w-4" />
              {safeCustStep > 0 ? et('Zurück') : et('Zurück zur Terminauswahl')}
            </Button>

            {/* Booking summary */}
            <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3" style={{ color: theme.text }}>{et('Ihre Buchung')}</h3>
                <div className="space-y-2 text-sm" style={{ color: theme.textMuted }}>
                  <div className="flex items-center gap-2">
                    <Dumbbell className="h-4 w-4" style={{ color: theme.accent }} />
                    <span>{selectedService?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" style={{ color: theme.accent }} />
                    <span>{formatDate(selectedDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" style={{ color: theme.accent }} />
                    <span>{etTime(selectedTime)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer form */}
            <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
              <CardHeader>
                <CardTitle style={{ color: theme.text }}>
                  {custKey === 'contact' ? et('Ihre Kontaktdaten')
                    : custKey === 'terms' ? et('AGB & Datenschutz')
                    : custKey === 'haftung' ? et('Haftungsausschluss')
                    : custKey === 'consent' ? et('Einverständnis-Erklärung')
                    : et('Bestätigen & Buchen')}
                </CardTitle>
                <CardDescription style={{ color: theme.textMuted }}>
                  {custKey === 'contact'
                    ? (waitlistMode
                        ? etp('Alle Zeiten um {0} sind belegt. Trag dich hier ein – wir buchen dich automatisch ein, sobald ein Platz frei wird.', [etTime(selectedTime)])
                        : et('Bitte geben Sie Ihre Daten ein.'))
                    : custKey === 'terms' ? et('Bitte akzeptiere unsere Bedingungen, um fortzufahren.')
                    : custKey === 'haftung' ? et('Bitte lies und unterschreibe den Haftungsausschluss.')
                    : custKey === 'consent' ? et('Bitte lies und unterschreibe die Einverständnis-Erklärung.')
                    : et('Prüfe deine Angaben und schliesse die Buchung ab.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {custKey === 'contact' && (<>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: theme.text }}>{et('Vorname *')}</Label>
                    <Input
                      placeholder="Max"
                      value={customerData.first_name}
                      onChange={(e) => setCustomerData({ ...customerData, first_name: e.target.value })}
                      className="mt-1"
                      style={{ backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}
                    />
                  </div>
                  <div>
                    <Label style={{ color: theme.text }}>{et('Nachname *')}</Label>
                    <Input
                      placeholder="Mustermann"
                      value={customerData.last_name}
                      onChange={(e) => setCustomerData({ ...customerData, last_name: e.target.value })}
                      className="mt-1"
                      style={{ backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}
                    />
                  </div>
                </div>
                
                <div>
                  <Label style={{ color: theme.text }}>{et('E-Mail *')}</Label>
                  <Input
                    type="email"
                    placeholder="max@beispiel.ch"
                    value={customerData.email}
                    onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                    className="mt-1"
                    style={{ backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}
                  />
                </div>
                
                <div>
                  <Label style={{ color: theme.text }}>{et('Telefon *')}</Label>
                  <PhoneInput
                    value={customerData.phone}
                    onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                    isDark={true}
                    data-testid="booking-phone-input"
                    className="mt-1"
                  />
                </div>

                {/* 09/2026: Partner-Probetraining — bei Probetrainings IMMER erlaubt
                    (auch bei nur 1 Platz, Kapazitätssperre wird umgangen), sonst
                    nur bei Services mit 2+ Plätzen. Spiegelt is_trial_service()
                    im Backend (public_booking_routes.py). */}
                {((selectedService?.is_trial ||
                   selectedService?.once_per_customer ||
                   /probe/i.test(selectedService?.name || '')) ||
                  (selectedService?.max_participants || 1) >= 2) && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: theme.bg }} data-testid="public-partner-section">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={withPartner}
                        onChange={(e) => setWithPartner(e.target.checked)}
                        className="h-4 w-4 rounded"
                        style={{ accentColor: theme.accent }}
                        data-testid="public-partner-toggle"
                      />
                      <span className="text-sm font-medium" style={{ color: theme.text }}>
                        👥 {et('Zu zweit trainieren — Partner gleich mitbringen')}
                      </span>
                    </label>
                    {withPartner && (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            placeholder={et('Vorname Partner *')}
                            value={partnerData.first_name}
                            onChange={(e) => setPartnerData(p => ({ ...p, first_name: e.target.value }))}
                            style={{ backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}
                            data-testid="public-partner-first-name"
                          />
                          <Input
                            placeholder={et('Nachname Partner')}
                            value={partnerData.last_name}
                            onChange={(e) => setPartnerData(p => ({ ...p, last_name: e.target.value }))}
                            style={{ backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}
                            data-testid="public-partner-last-name"
                          />
                        </div>
                        <Input
                          type="email"
                          placeholder={et('E-Mail Partner')}
                          value={partnerData.email}
                          onChange={(e) => setPartnerData(p => ({ ...p, email: e.target.value }))}
                          style={{ backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}
                          data-testid="public-partner-email"
                        />
                        <PhoneInput
                          value={partnerData.phone}
                          onChange={(e) => setPartnerData(p => ({ ...p, phone: e.target.value }))}
                          isDark={true}
                          data-testid="public-partner-phone"
                        />
                        <p className="text-xs" style={{ color: theme.textMuted }}>
                          {et('Dein Partner erhält sofort eine Einladung per WhatsApp/E-Mail und wird nach der Registrierung automatisch in denselben Termin eingetragen.')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Lead-Quelle — Pflichtfeld bei Probetrainings (06/2026) */}
                {(selectedService?.is_trial || selectedService?.once_per_customer || /probe/i.test(selectedService?.name || '')) && (
                  <div data-testid="public-referral-source-section">
                    <Label style={{ color: theme.text }}>{et('Wie hast du von uns erfahren? *')}</Label>
                    <select
                      value={customerData.referral_source_id}
                      onChange={(e) => setCustomerData({ ...customerData, referral_source_id: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md border px-3 text-sm"
                      style={{ backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}
                      data-testid="public-referral-source-select"
                    >
                      <option value="" style={{ color: '#000' }}>{et('Bitte auswählen …')}</option>
                      {referralSources.map((rs) => (
                        <option key={rs.id} value={rs.id} style={{ color: '#000' }}>{rs.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                </>)}

                {/* Terms checkbox */}
                {custKey === 'terms' && (
                <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: theme.bg }}>
                  <input
                    type="checkbox"
                    id="terms"
                    checked={customerData.accepted_terms}
                    onChange={(e) => setCustomerData({ ...customerData, accepted_terms: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded"
                    style={{ accentColor: theme.accent }}
                  />
                  <label htmlFor="terms" className="text-sm" style={{ color: theme.textMuted }}>
                    {et('Ich akzeptiere die')}{' '}
                    <button
                      type="button"
                      onClick={loadTerms}
                      className="underline font-medium"
                      style={{ color: theme.accent }}
                    >
                      {et('AGB')}
                    </button>
                    {' '}{et('und die')}{' '}
                    <button
                      type="button"
                      onClick={loadPrivacy}
                      className="underline font-medium"
                      style={{ color: theme.accent }}
                    >
                      {et('Datenschutzerklärung')}
                    </button>
                    {' '}*
                  </label>
                </div>
                )}

                {/* Haftungsausschluss — IMMER Pflicht, direkt beim Buchen unterschreiben */}
                {custKey === 'haftung' && haftungForm && (
                  <div className="p-4 rounded-lg space-y-3 bg-zinc-900 border" style={{ borderColor: theme.border }} data-testid="public-booking-haftung-section">
                    <p className="font-semibold text-white text-sm">{et('✍️ Haftungsausschluss:')} {haftungForm.title}</p>
                    <ConsentSignSection form={haftungForm} onChange={setHaftungPayload} />
                  </div>
                )}

                {/* Einverständnis-Erklärung (Kryo/EMS) — Pflicht, direkt beim Buchen unterschreiben */}
                {custKey === 'consent' && consentForm && (
                  <div className="p-4 rounded-lg space-y-3 bg-zinc-900 border" style={{ borderColor: theme.border }} data-testid="public-booking-consent-section">
                    <p className="font-semibold text-white text-sm">{et('✍️ Einverständnis-Erklärung:')} {consentForm.title}</p>
                    <ConsentSignSection form={consentForm} onChange={setConsentPayload} />
                  </div>
                )}

                {custKey === 'review' && (<>
                {/* reCAPTCHA */}
                {settings?.recaptcha_enabled && settings?.recaptcha_site_key && (
                  <div className="flex justify-center p-3 rounded-lg" style={{ backgroundColor: theme.bg }}>
                    <div id="recaptcha-container" />
                  </div>
                )}

                <Button 
                  className="w-full"
                  disabled={booking || !customerData.accepted_terms || ((selectedService?.is_trial || selectedService?.once_per_customer || /probe/i.test(selectedService?.name || '')) && !customerData.referral_source_id) || (settings?.recaptcha_enabled && !recaptchaToken) || (!waitlistMode && haftungForm && !haftungPayload) || (!waitlistMode && consentForm && !consentPayload)}
                  onClick={handleBooking}
                  style={{ backgroundColor: theme.accent, color: '#000' }}
                >
                  {booking
                    ? (waitlistMode ? et('Wird eingetragen...') : et('Wird gebucht...'))
                    : (waitlistMode ? et('Auf Warteliste setzen') : et('Jetzt buchen'))}
                </Button>
                
                {settings?.recaptcha_enabled && !recaptchaToken && (
                  <p className="text-xs text-center" style={{ color: theme.textMuted }}>
                    {et('Bitte bestätigen Sie, dass Sie kein Roboter sind')}
                  </p>
                )}
                </>)}

                {custKey !== 'review' && (
                  <Button
                    className="w-full"
                    onClick={goNextCust}
                    style={{ backgroundColor: theme.accent, color: '#000' }}
                    data-testid="booking-cust-next"
                  >
                    {et('Weiter')}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Terms Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
          <DialogHeader>
            <DialogTitle style={{ color: theme.text }}>{et('Allgemeine Geschäftsbedingungen (AGB)')}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed" style={{ color: theme.textMuted }}>{termsContent}</pre>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={() => setShowTerms(false)} style={{ backgroundColor: theme.accent, color: '#000' }}>
              {et('Schliessen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Dialog */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
          <DialogHeader>
            <DialogTitle style={{ color: theme.text }}>{et('Datenschutzerklärung')}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed" style={{ color: theme.textMuted }}>{privacyContent}</pre>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={() => setShowPrivacy(false)} style={{ backgroundColor: theme.accent, color: '#000' }}>
              {et('Schliessen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicBooking;
